import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import theme from './admin/adminTheme';
import AdminSpinner from './admin/components/AdminSpinner';
import AdminPagination from './admin/components/AdminPagination';

import { getPerfiles } from '../services/authService';
import {
  getPresidenteMe,
  listarUsuariosPresidente,
  asociarUsuarioAPresidente,
  quitarUsuarioDePresidente,
} from '../services/presidenteClubService';

import { ToastProvider, useToast } from './admin/context/ToastContext';

const PresidentClubInner = () => {
  const navigate = useNavigate();
  const { showToast } = useToast();

  const [loading, setLoading] = useState(true);
  const [perfiles, setPerfiles] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [error, setError] = useState(null);
  const [actionLoadingRut, setActionLoadingRut] = useState(null);

  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);

  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    total_pages: 1,
    has_next: false,
    has_prev: false,
  });

  const esPresidente = useMemo(
    () => Array.isArray(perfiles) && perfiles.includes('presidente_club'),
    [perfiles]
  );

  const clubPresidente = useMemo(() => ({ club_id: null }), []);

  const [clubInfo, setClubInfo] = useState(null);

  const cargarUsuarios = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await listarUsuariosPresidente({ page, limit: 20, search });
      setUsuarios(data.items || []);
      if (data.pagination) setPagination(data.pagination);
    } catch (e) {
      setError(e.message || 'Error al cargar usuarios');
      setUsuarios([]);
    } finally {
      setLoading(false);
    }
  }, [page, search]);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const perfilesResp = await getPerfiles();
        if (cancelled) return;
        setPerfiles(perfilesResp || []);

        if (!perfilesResp || !perfilesResp.includes('presidente_club')) {
          setError('No autorizado: debes tener perfil presidente_club.');
          setUsuarios([]);
          setLoading(false);
          return;
        }

        const me = await getPresidenteMe();
        if (cancelled) return;
        setClubInfo(me?.club || null);
      } catch (e) {
        if (cancelled) return;
        setError(e.message || 'Error al cargar información de presidente');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (!esPresidente) return;
    cargarUsuarios();
  }, [cargarUsuarios, esPresidente]);

  const handleSearch = (e) => {
    e.preventDefault();
    setPage(1);
    setSearch(searchInput);
  };

  const handleClearSearch = () => {
    setSearchInput('');
    setSearch('');
    setPage(1);
  };

  const handleAsociar = async (rut) => {
    if (actionLoadingRut) return;
    setActionLoadingRut(rut);
    try {
      await asociarUsuarioAPresidente(rut);
      showToast('Asociación actualizada', 'success');
      await cargarUsuarios();
    } catch (e) {
      showToast(e.message || 'Error al asociar', 'error');
    } finally {
      setActionLoadingRut(null);
    }
  };

  const handleQuitar = async (rut) => {
    if (actionLoadingRut) return;
    setActionLoadingRut(rut);
    try {
      await quitarUsuarioDePresidente(rut);
      showToast('Asociación removida', 'success');
      await cargarUsuarios();
    } catch (e) {
      showToast(e.message || 'Error al quitar asociación', 'error');
    } finally {
      setActionLoadingRut(null);
    }
  };

  if (!esPresidente) {
    return (
      <div style={theme.container}>
        {error ? <div style={theme.error}>{error}</div> : null}
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <button style={theme.btnGhost} onClick={() => navigate('/profile')}>Volver</button>
        </div>
      </div>
    );
  }

  return (
    <div style={theme.container}>
      <div style={theme.pageHeader}>
        <div>
          <h2 style={theme.title}>Presidente de club</h2>
          <div style={{ color: '#666', marginTop: 6 }}>
            {clubInfo?.nombre ? `Club: ${clubInfo.nombre}` : 'Administrar membresías de tu club'}
          </div>
        </div>

        <form onSubmit={handleSearch} style={theme.searchContainer}>
          <input
            type="text"
            placeholder="Buscar por RUT, nombre o email..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            style={theme.searchInput}
            aria-label="Buscar usuarios"
          />
          <button type="submit" style={theme.btnGhost}>Buscar</button>
          {search ? (
            <button type="button" style={theme.btnGhost} onClick={handleClearSearch}>
              Limpiar
            </button>
          ) : null}
        </form>
      </div>

      {error && <div style={theme.error}>{error}</div>}
      {loading ? (
        <AdminSpinner label="Cargando usuarios..." />
      ) : (
        <>
          <div style={{ overflowX: 'auto' }}>
            <table style={theme.table}>
              <thead>
                <tr>
                  <th style={theme.th}>RUT</th>
                  <th style={theme.th}>Nombre</th>
                  <th style={theme.th}>Email</th>
                  <th style={theme.th}>Estado</th>
                  <th style={theme.th}>Acción</th>
                </tr>
              </thead>
              <tbody>
                {usuarios.length === 0 ? (
                  <tr>
                    <td colSpan="5" style={{ ...theme.td, textAlign: 'center', color: '#666' }}>
                      {search ? 'No hay resultados con los filtros actuales' : 'No hay usuarios'}
                    </td>
                  </tr>
                ) : (
                  usuarios.map((u) => (
                    <tr key={u.rut}>
                      <td style={theme.td}>{u.rut}</td>
                      <td style={theme.td}>
                        {u.nombre} {u.apellido}
                      </td>
                      <td style={theme.td}>{u.email || '—'}</td>
                      <td style={theme.td}>
                        {u.en_mi_club ? (
                          <span style={{ ...theme.badge, ...theme.badgeActivo }}>
                            En tu club
                          </span>
                        ) : (
                          <span style={{ ...theme.badge, ...theme.badgeInactivo }}>
                            Fuera de tu club
                          </span>
                        )}
                      </td>
                      <td style={theme.td}>
                        {u.en_mi_club ? (
                          <button
                            style={theme.btnDanger}
                            disabled={actionLoadingRut === u.rut}
                            onClick={() => handleQuitar(u.rut)}
                          >
                            {actionLoadingRut === u.rut ? 'Quitando...' : 'Quitar'}
                          </button>
                        ) : (
                          <button
                            style={theme.btnPrimary}
                            disabled={actionLoadingRut === u.rut}
                            onClick={() => handleAsociar(u.rut)}
                          >
                            {actionLoadingRut === u.rut ? 'Asociando...' : 'Asociar'}
                          </button>
                        )}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {pagination.total > 0 ? (
            <AdminPagination
              page={pagination.page}
              totalPages={pagination.total_pages}
              total={pagination.total}
              pageSize={pagination.limit}
              hasPrev={pagination.has_prev}
              hasNext={pagination.has_next}
              onPrev={() => setPage((p) => Math.max(1, p - 1))}
              onNext={() => setPage((p) => p + 1)}
              label="usuarios"
            />
          ) : null}
        </>
      )}
    </div>
  );
};

const PresidentClubPage = () => {
  return (
    <ToastProvider>
      <PresidentClubInner />
    </ToastProvider>
  );
};

export default PresidentClubPage;

